import { useState } from "react";
import { Box, Button, Card, CardActions, CardContent, Stack, TextField, Typography } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ConfirmDialog, EmptyState, LoadingButton, formatCnpj, formatRelativeFromNow } from "@safrater/ui";
import ApprovalIcon from "@mui/icons-material/Approval";
import { api } from "../../api/client";

interface PendingApproval {
  id: string;
  status: "pending";
  createdAt: string;
  institution: { id: string; name: string; cnpj: string };
  requestedBy: { id: string; name: string; email: string };
}

export function ApprovalsPage() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["approvals"],
    queryFn: async () => (await api.get<PendingApproval[]>("/institutions/approvals/pending")).data,
  });

  const [rejectTarget, setRejectTarget] = useState<PendingApproval | null>(null);
  const [reason, setReason] = useState("");

  const approve = useMutation({
    mutationFn: (id: string) => api.post(`/institutions/approvals/${id}/approve`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["approvals"] });
      qc.invalidateQueries({ queryKey: ["institutions"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });

  const reject = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => api.post(`/institutions/approvals/${id}/reject`, { reason }),
    onSuccess: () => {
      setRejectTarget(null);
      setReason("");
      qc.invalidateQueries({ queryKey: ["approvals"] });
    },
  });

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Aprovações pendentes</Typography>
      {!data || data.length === 0 ? (
        <EmptyState
          title="Nada pendente"
          description="Quando uma nova instituição se cadastrar, ela aparecerá aqui."
          icon={<ApprovalIcon fontSize="inherit" />}
        />
      ) : (
        <Stack spacing={2}>
          {data.map((a) => (
            <Card key={a.id}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} flexWrap="wrap">
                  <Box>
                    <Typography variant="h6">{a.institution.name}</Typography>
                    <Typography variant="body2" color="text.secondary">CNPJ {formatCnpj(a.institution.cnpj)}</Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Solicitado por <strong>{a.requestedBy.name}</strong> ({a.requestedBy.email}) · {formatRelativeFromNow(a.createdAt)}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
              <CardActions sx={{ px: 2, pb: 2, gap: 1, justifyContent: "flex-end" }}>
                <Button color="error" onClick={() => setRejectTarget(a)}>Recusar</Button>
                <LoadingButton variant="contained" loading={approve.isPending} onClick={() => approve.mutate(a.id)}>
                  Aprovar
                </LoadingButton>
              </CardActions>
            </Card>
          ))}
        </Stack>
      )}

      <ConfirmDialog
        open={Boolean(rejectTarget)}
        title="Recusar solicitação"
        description="Informe o motivo da recusa. Essa informação ficará registrada na auditoria."
        severity="error"
        confirmLabel="Recusar"
        loading={reject.isPending}
        onConfirm={() => {
          if (rejectTarget && reason.trim()) reject.mutate({ id: rejectTarget.id, reason: reason.trim() });
        }}
        onCancel={() => { setRejectTarget(null); setReason(""); }}
      />
      {rejectTarget && (
        <Box sx={{ display: "none" }}>
          <TextField
            multiline
            minRows={3}
            label="Motivo"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Box>
      )}
    </Box>
  );
}
